import { useMemo, useCallback, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Sun, CloudSun, Sunset, Moon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { chatRemarkPlugins, chatRehypePlugins } from '@/lib/markdown';
import type { CampaignJournal, JournalEvent } from '@danda/shared';
import type { LucideIcon } from 'lucide-react';

const journalMarkdownComponents = {
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="mb-1.5 last:mb-0">{children}</p>
  ),
} as const;

function timeOfDayIcon(hour: number): { Icon: LucideIcon; color: string } {
  if (hour >= 6 && hour <= 11) return { Icon: Sun, color: 'text-amber-400' };
  if (hour >= 12 && hour <= 17) return { Icon: CloudSun, color: 'text-sky-400' };
  if (hour >= 18 && hour <= 21) return { Icon: Sunset, color: 'text-orange-400' };
  return { Icon: Moon, color: 'text-indigo-400' };
}

type DayGroup = {
  day: number;
  events: JournalEvent[];
};

function groupByDay(events: JournalEvent[]): DayGroup[] {
  const dayMap = new Map<number, JournalEvent[]>();

  for (const event of events) {
    const day = event.game_time?.day ?? -1;
    const existing = dayMap.get(day);
    if (existing) {
      existing.push(event);
    } else {
      dayMap.set(day, [event]);
    }
  }

  return Array.from(dayMap.entries())
    .sort(([a], [b]) => b - a)
    .map(([day, dayEvents]) => ({
      day,
      events: dayEvents.sort((a, b) => {
        const aTime = a.game_time;
        const bTime = b.game_time;
        if (!aTime || !bTime) return 0;
        if (aTime.hour !== bTime.hour) return aTime.hour - bTime.hour;
        return aTime.minute - bTime.minute;
      }),
    }));
}

function formatTime(event: JournalEvent): string | null {
  if (!event.game_time) return null;
  const h = String(event.game_time.hour).padStart(2, '0');
  const m = String(event.game_time.minute).padStart(2, '0');
  return `${h}:${m}`;
}

function DayHeader({
  day,
  collapsed,
  onToggle,
}: {
  day: number;
  collapsed: boolean;
  onToggle: (day: number) => void;
}) {
  if (day === -1) return null;
  return (
    <button
      type="button"
      onClick={() => onToggle(day)}
      className="flex items-center gap-3 py-3 px-1 w-full cursor-pointer hover:opacity-80 transition-opacity"
    >
      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-600/40 to-transparent" />
      <span
        className={cn(
          'font-character text-sm tracking-wider uppercase transition-colors',
          collapsed ? 'text-amber-500/50' : 'text-amber-500/80'
        )}
      >
        Day {day + 1}
      </span>
      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-600/40 to-transparent" />
    </button>
  );
}

function JournalEventCard({ event }: { event: JournalEvent }) {
  const isMajor = event.importance === 'major';
  const isCritical = event.importance === 'critical';
  const timeIcon = event.game_time ? timeOfDayIcon(event.game_time.hour) : null;

  return (
    <div
      className={cn(
        'group flex gap-3 px-3 py-2 rounded-lg transition-colors',
        'bg-card/40 backdrop-blur-sm border border-border/30',
        'animate-in fade-in-0 slide-in-from-bottom-1 duration-300',
        isMajor && 'border-amber-600/40',
        isCritical && 'border-amber-500/60 shadow-[0_0_8px_rgba(245,158,11,0.15)]'
      )}
    >
      <div className="flex flex-col items-center justify-center gap-0.5 min-w-[3rem] self-center">
        {(() => {
          const time = formatTime(event);
          return time ? (
            <span className="text-xs text-muted-foreground font-mono">{time}</span>
          ) : null;
        })()}
        {timeIcon ? (
          <timeIcon.Icon className={cn('h-4 w-4', timeIcon.color)} />
        ) : (
          <span className="text-base leading-none">📜</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-narrative text-sm text-foreground/90 leading-relaxed">
          <ReactMarkdown
            remarkPlugins={chatRemarkPlugins}
            rehypePlugins={chatRehypePlugins}
            components={journalMarkdownComponents}
          >
            {event.summary}
          </ReactMarkdown>
        </div>
        {event.details && (
          <div className="text-xs text-muted-foreground mt-1 italic">
            <ReactMarkdown
              remarkPlugins={chatRemarkPlugins}
              rehypePlugins={chatRehypePlugins}
              components={journalMarkdownComponents}
            >
              {event.details}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground px-8">
      <p className="text-sm text-center py-4">Nothing yet — go adventure!</p>
    </div>
  );
}

interface JournalEventListProps {
  journal: CampaignJournal;
  className?: string;
}

export function JournalEventList({ journal, className }: JournalEventListProps) {
  const events = useMemo(() => {
    if (!journal?.events?.length) return [];
    return journal.events.filter((e) => e.game_time);
  }, [journal?.events]);

  const dayGroups = useMemo(() => groupByDay(events), [events]);

  // Auto-collapse all days except the most recent when there are multiple days
  const [collapsedDays, setCollapsedDays] = useState<Set<number>>(() => {
    if (dayGroups.length <= 1) return new Set();
    return new Set(dayGroups.slice(1).map((g) => g.day));
  });

  const toggleDay = useCallback((day: number) => {
    setCollapsedDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  }, []);

  if (!journal?.events?.length || dayGroups.length === 0) return <EmptyState />;

  return (
    <div className={cn('space-y-1', className)}>
      {dayGroups.map((group) => {
        const collapsed = collapsedDays.has(group.day);
        return (
          <div key={group.day}>
            <DayHeader day={group.day} collapsed={collapsed} onToggle={toggleDay} />
            {!collapsed && (
              <div className="space-y-1.5">
                {group.events.map((event) => (
                  <JournalEventCard key={event.event_id} event={event} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
